import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Alert,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  SkeletonRows,
  TextField,
  mutedTextClass,
} from '@/components/layout/ui'
import { orgsApi } from '@/lib/api'
import { cn } from '@/lib/cn'
import { fieldErrorFor, isFormLevel, messageFor } from '@/lib/errors'
import { formatDate, titleCase } from '@/lib/format'
import { queryKeys } from '@/lib/queryClient'
import { useSession } from '@/lib/session'
import { useWorkspace } from '@/lib/workspace'

/** `/{slug}/settings/general` — rename the workspace, change its address, delete it. */
export default function GeneralSettingsPage() {
  const workspace = useWorkspace()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { refresh } = useSession()

  const org = useQuery({
    queryKey: queryKeys.org(workspace.slug),
    queryFn: ({ signal }) => orgsApi.get(workspace.slug, { signal }),
  })

  const [name, setName] = useState(workspace.membership.name)
  const [slug, setSlug] = useState(workspace.slug)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Re-seed the form once the authoritative record arrives, so a rename made in
  // another tab is not silently overwritten by a stale field value.
  useEffect(() => {
    if (org.data) {
      setName(org.data.name)
      setSlug(org.data.slug)
    }
  }, [org.data])

  const canEdit = workspace.can('org.update')
  const canDelete = workspace.can('org.delete') && !workspace.isPersonal

  const save = useMutation({
    mutationFn: () => orgsApi.update(workspace.slug, { name: name.trim(), slug: slug.trim() }),
    onSuccess: async (updated) => {
      await refresh()
      await queryClient.invalidateQueries({ queryKey: queryKeys.orgs })
      if (updated.slug !== workspace.slug) {
        // The address is part of every URL in the shell; follow it rather than
        // leaving the user on a slug that no longer resolves.
        navigate(`/${updated.slug}/settings/general`, { replace: true })
      }
    },
  })

  const remove = useMutation({
    mutationFn: () => orgsApi.remove(workspace.slug),
    onSuccess: async () => {
      setConfirmOpen(false)
      queryClient.clear()
      await refresh()
      navigate('/', { replace: true })
    },
  })

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    save.mutate()
  }

  // Compared against the loaded record, not the session summary: before the
  // record arrives there is nothing to diff against and nothing to save.
  const dirty =
    org.data !== undefined &&
    (name.trim() !== org.data.name || slug.trim() !== org.data.slug)

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Workspace"
          description={
            canEdit
              ? 'The name people see, and the address in every link.'
              : 'Only admins can change these.'
          }
        />
        <form onSubmit={handleSubmit} className="space-y-4 p-5" noValidate>
          <TextField
            label="Name"
            value={name}
            disabled={!canEdit || save.isPending}
            onChange={(event) => setName(event.target.value)}
            error={fieldErrorFor(save.error, 'name')}
          />
          <TextField
            label="Address"
            value={slug}
            disabled={!canEdit || save.isPending}
            spellCheck={false}
            autoCapitalize="off"
            onChange={(event) => setSlug(event.target.value)}
            hint={`Links look like /app/${slug || 'workspace'}. Changing it breaks existing links.`}
            error={fieldErrorFor(save.error, 'slug')}
          />

          {save.error && isFormLevel(save.error, ['name', 'slug']) ? (
            <Alert tone="danger">{messageFor(save.error)}</Alert>
          ) : null}
          {save.isSuccess && !dirty ? <Alert tone="success">Workspace updated.</Alert> : null}

          {canEdit ? (
            <div className="flex justify-end">
              <Button type="submit" variant="primary" loading={save.isPending} disabled={!dirty}>
                Save changes
              </Button>
            </div>
          ) : null}
        </form>
      </Card>

      <Card>
        <CardHeader title="Details" />
        {org.isPending ? (
          <div className="p-5">
            <SkeletonRows rows={3} />
          </div>
        ) : org.isError ? (
          <div className="p-5">
            <Alert tone="danger">{messageFor(org.error)}</Alert>
          </div>
        ) : (
          <dl className="divide-y divide-ink-200 text-sm dark:divide-ink-800">
            <Row label="Type" value={titleCase(org.data.type)} />
            <Row label="Plan" value={titleCase(org.data.plan)} />
            <Row label="Your role" value={titleCase(workspace.role)} />
            <Row label="Members" value={String(org.data.member_count ?? '—')} />
            <Row label="Created" value={formatDate(org.data.created_at)} />
            <Row label="Workspace ID" value={org.data.id} mono />
          </dl>
        )}
      </Card>

      {canDelete ? (
        <Card className="border-red-200 dark:border-red-900">
          <CardHeader
            title="Delete workspace"
            description="Members lose access immediately. Projects and issues are retained but hidden."
          />
          <div className="p-5">
            {remove.error ? (
              <Alert tone="danger" className="mb-4">
                {messageFor(remove.error)}
              </Alert>
            ) : null}
            <Button variant="danger" onClick={() => setConfirmOpen(true)}>
              Delete {workspace.membership.name}
            </Button>
          </div>
        </Card>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => remove.mutate()}
        title="Delete this workspace?"
        description="Everyone loses access straight away. This cannot be undone from the web app."
        confirmLabel="Delete workspace"
        confirmWord={workspace.membership.name}
        loading={remove.isPending}
        error={remove.error ? messageFor(remove.error) : null}
      />
    </div>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3">
      <dt className={cn(mutedTextClass)}>{label}</dt>
      <dd className={cn('truncate font-medium', mono && 'font-mono text-xs')}>{value}</dd>
    </div>
  )
}
